import type { StatusFailure } from '../data/use-status'

/*
 * V1_2_SPEC.md §5.3 — the failure mode that matters most, built before the happy
 * path was polished because it is the state this page exists for and the one
 * that gets skipped.
 *
 *   "A backend/control-plane failure must never be interpreted as 'everything is
 *    operational' merely because the status API cannot respond."
 *
 * The status application and the status API are on different hosts by design
 * (D5). That is what makes this condition reachable at all — and it is why the
 * shell, the header and the last-updated line are still on screen around this
 * banner rather than being replaced by it.
 *
 * ── Naming the class of failure, and nothing more ──────────────────────────
 *
 * §5.3 asks for the class — network, timeout, server error — and forbids a stack
 * trace. The reason to name it is not diagnostics; it is that the three have
 * different meanings *for the reader*. A network failure might be their own
 * connection, and a reader who knows that will check it. A timeout or a 5xx is
 * ours, and a reader who knows that will stop looking at their own logs. Telling
 * them "something went wrong" leaves them debugging our outage.
 *
 * The HTTP status is included for a server error and nothing else — `502` is a
 * fact a reader can quote to support, and it discloses nothing §4.2 protects.
 */

const HEADLINE = 'System status is currently unavailable'

function explain(failure: StatusFailure): string {
  switch (failure.kind) {
    case 'network':
      return 'This page could not reach the PushCaster API. That may be your connection, or ours.'
    case 'timeout':
      return 'The PushCaster API did not respond in time. The platform may be under load or unreachable.'
    case 'server':
      return failure.status === null
        ? 'The PushCaster API returned an error.'
        : `The PushCaster API returned an error (HTTP ${failure.status}).`
    case 'shape':
      return 'The PushCaster API returned a response this page does not recognise.'
  }
}

/**
 * What is on screen beneath this banner, which decides what the banner may claim.
 *
 * Three states and not two, and the middle one is the reason. The first version
 * of this took a boolean — "is there earlier data?" — and said "every component
 * below now reads not reporting" whenever there was. But a failed poll does not
 * demote anything on its own: `use-status.ts` demotes on **staleness**, two
 * refresh intervals later, deliberately, so a transient blip does not flicker
 * the page. For the sixty seconds in between, the banner was describing a
 * demotion that had not happened, above rows that still read "Degraded
 * performance". Caught in a browser with the API actually stopped.
 *
 * A page whose job is to be believed cannot narrate its own state incorrectly,
 * so the three cases are named and each gets its own sentence.
 */
export type DataBeneath =
  /** No payload has ever arrived. Nothing to demote and nothing to show. */
  | 'none'
  /** A payload is on screen and is inside the freshness window. It has not been
   *  confirmed since this failure and may yet be. */
  | 'unconfirmed'
  /** A payload is on screen and every state in it has been replaced by
   *  `unknown`. */
  | 'demoted'

const BENEATH: Record<DataBeneath, string> = {
  none: 'No component or region state is available, and none is being assumed. Nothing below is being reported as operational.',
  unconfirmed:
    'The states below are from the last successful update and have not been confirmed since. If this continues they will be marked “not reporting”.',
  demoted:
    'Every component and region below now reads “not reporting”. The states shown before this failure are not being treated as current.',
}

export function Unavailable({
  failure,
  onRetry,
  retrying,
  beneath,
}: {
  readonly failure: StatusFailure
  readonly onRetry: () => void
  readonly retrying: boolean
  readonly beneath: DataBeneath
}) {
  return (
    <section
      /*
       * `alert` and not `status`: this interrupts, and a reader using a screen
       * reader should be told without having to go looking. It is announced once
       * on appearance, which is right — the retry button is what re-announces it.
       */
      role="alert"
      className="border border-critical bg-critical-tint p-4"
    >
      <h2 className="text-sm font-medium text-critical">{HEADLINE}</h2>
      <p className="mt-1 text-sm text-text-secondary">{explain(failure)}</p>
      <p className="mt-2 text-sm text-text-secondary">{BENEATH[beneath]}</p>
      <button
        type="button"
        onClick={onRetry}
        disabled={retrying}
        className="mt-3 rounded-xs border border-border-strong px-3 py-1.5 text-sm text-text-primary hover:bg-surface-raised disabled:text-text-disabled"
      >
        {retrying ? 'Retrying…' : 'Retry'}
      </button>
    </section>
  )
}
